import React from 'react'
import {
  TextField,
  Button,
  Box,
  ListItem,
  List,
  CircularProgress,
  Paper,
  Typography,
  Select,
  MenuItem,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  FormControl,
  IconButton,
  Grid,
  Table,
} from '@mui/material'
import { IBatch, IPremix, Premix } from 'interfaces/IPremix'
import { IProduction } from 'interfaces/IProduction'
import { IReduxState } from 'interfaces/IReduxState'
import { useSelector } from 'react-redux'
import API from '@aws-amplify/api'
import LoadPremixAutocomplete from 'shared/autocomplete/loadPremixAutocomplete'
import LoadIngredientAutocomplete from 'shared/autocomplete/loadIngredientAutocomplete'
import { formatDecimalAuto } from 'shared/numberFormatter'
import { IIngredient } from 'interfaces/IIngredient'
import AddIcon from '@mui/icons-material/Add'
import ClearIcon from '@mui/icons-material/Clear'

const typeDropdownValues = [
  { key: 'F', value: 'Sabun' },
  { key: 'T', value: 'Toner' },
  { key: 'S', value: 'Serum' },
  { key: 'D', value: 'BB Cream/CC Cream' },
  { key: 'N', value: 'Night Cream/Day Cream' },
  { key: 'B', value: 'Body Lotion' },
  { key: 'C', value: 'Scrub' },
  { key: 'M', value: 'Micellar Water' },
  { key: 'O', value: 'Massage Oil' },
  { key: 'L', value: 'Lipstik' },
  { key: 'W', value: 'TWC' },
  { key: 'P', value: 'Loose Powder' },
  { key: 'G', value: 'Cleansing Milk' },
  { key: 'J', value: 'Gel' },
]

const productionLotDropdownValues = [
  { key: 'A', value: 'Seri Normal' },
  { key: 'B', value: 'Seri Acne' },
  { key: 'R', value: 'Bulk Rework/RnD' },
]

const STOCK_TOLERANCE = 0.000001

function hasEnoughStock(required: number, available: number) {
  return required <= available + STOCK_TOLERANCE
}

export default function BatchForm(props: {
  batchData: IBatch
  handleBatchFieldChange: (value: any, field: string) => void
  saveBatch?: (e: React.SyntheticEvent) => void
  updateBatch?: (e: React.SyntheticEvent) => void
  productionData: IProduction
  isLoading?: boolean
  allowProductionDateChange?: boolean
  isEditing?: boolean
  isQuantityInitiallyNull?: boolean
}) {
  const [typeDropdownValue, setTypeDropdownValue] = React.useState<
    null | string
  >(null)

  // State for rework premix batch options
  const [reworkPremixBatchOptions, setReworkPremixBatchOptions] =
    React.useState<{ [key: number]: IBatch[] }>({})
  // State for nested premix batch options (for ruahan di ruahan)
  const [nestedPremixBatchOptions, setNestedPremixBatchOptions] =
    React.useState<{ [key: string]: IBatch[] }>({})
  const [productionLotDropdownValue, setProductionLotDropdownValue] =
    React.useState<null | string>(null)
  const [batchDigits, setBatchDigits] = React.useState<string>('')
  const [relatedBatches, setRelatedBatches] = React.useState<IBatch[]>([])
  const [premixData, setPremixData] = React.useState<IPremix>(new Premix())
  const [selectedMonth, setSelectedMonth] = React.useState<string>(() => {
    const month = new Date().getMonth() + 1
    return month < 10 ? '0' + month : '' + month
  })
  const [selectedYear, setSelectedYear] = React.useState<string>(() => {
    return new Date().getFullYear().toString().substring(2)
  })

  // Store the original batch number when editing to correctly identifying the batch
  // even if the user changes the batch number field.
  const [originalBatchNumber, setOriginalBatchNumber] = React.useState<string | null>(null)

  const user = useSelector((state: IReduxState) => state.currentUser || {})

  const {
    batchData,
    productionData,
    handleBatchFieldChange,
    updateBatch,
    saveBatch,
    isLoading,
    isEditing,
    isQuantityInitiallyNull,
  } = props

  // Initialize originalBatchNumber on first edit load
  React.useEffect(() => {
    if (isEditing && !originalBatchNumber && batchData.batchNumber) {
      setOriginalBatchNumber(batchData.batchNumber)
    }
  }, [isEditing, batchData.batchNumber, originalBatchNumber])

  const getValidationErrors = () => {
    const errors: string[] = []
    if (!batchData.batchSize) errors.push('Batch Size wajib diisi')
    if (Number(batchData.batchSize) >= 1000000)
      errors.push('Batch Size tidak boleh > 1,000,000')
    if (premixData.department?.toLowerCase() !== 'ot' && !typeDropdownValue)
      errors.push('Jenis Sediaan wajib dipilih')
    if (!productionLotDropdownValue) errors.push('Lot Produksi wajib dipilih')
    if (!batchDigits) errors.push('3 Digit No. Bets wajib diisi')
    if (batchDigits.length !== 3 || !/^\d+$/.test(batchDigits))
      errors.push('3 Digit No. Bets harus berupa 3 angka')

    // Sum validation
    const totalExisting = relatedBatches
      .filter((b: IBatch) => !b.isVirtual)
      .reduce((acc: number, batch: IBatch) => {
      // Exclude the current batch if we are editing it
      if (isEditing) {
        // Use originalBatchNumber if available (captured on mount)
        if (originalBatchNumber && batch.batchNumber === originalBatchNumber) return acc
        // Fallback to current batchNumber if original not captured (e.g. race condition or new batch)
        if (!originalBatchNumber && batch.batchNumber === batchData.batchNumber) return acc
      }
      return acc + Number(batch.batchSize ?? 0)
    }, 0)

    const currentSize = Number(batchData.batchSize ?? 0)
    const totalWithCurrent = totalExisting + currentSize
    const requestedQty = Number(productionData.premixQuantity ?? 0)

    if (totalWithCurrent > requestedQty + 0.000001) {
      errors.push(
        `Total batch size (${totalWithCurrent}) melebihi Qty Requested (${requestedQty})`
      )
    }

    // Duplicate batch number validation
    if (!isEditing) {
      const isDuplicate = relatedBatches.some(
        (b) => b.batchNumber === batchData.batchNumber
      )
      if (isDuplicate) {
        errors.push(
          `No. Bets ${batchData.batchNumber} sudah ada di daftar PBOS ini`
        )
      }
    }

    if (productionLotDropdownValue === 'R' || (premixData.department?.toLowerCase() === 'ot' && productionLotDropdownValue === 'T')) {
      // Rework mode validation
      const hasIngredients = (batchData.reworkIngredients || []).length > 0
      const hasPremixes = (batchData.reworkPremixes || []).length > 0
      if (!hasIngredients && !hasPremixes) {
        errors.push(
          premixData.department?.toLowerCase() === 'ot'
            ? 'Custom Formula wajib diisi (minimal 1 bahan atau 1 ruahan)'
            : 'Rework Formula wajib diisi (minimal 1 bahan atau 1 ruahan)'
        )
      }

      batchData.reworkIngredients?.forEach((item, idx) => {
        if (!item.ingredientId)
          errors.push(`Bahan Baku baris ${idx + 1} belum dipilih`)
        if (Number(item.quantity || 0) <= 0)
          errors.push(`Bahan Baku baris ${idx + 1} qty harus > 0`)
        if (
          !hasEnoughStock(
            Number(item.quantity || 0),
            Number(item.stock || 0)
          )
        )
          errors.push(`Stok Bahan Baku baris ${idx + 1} tidak cukup`)
      })

      batchData.reworkPremixes?.forEach((item, idx) => {
        if (!item.premixId) errors.push(`Ruahan baris ${idx + 1} belum dipilih`)
        if (!item.batchNumber)
          errors.push(`Batch Ruahan baris ${idx + 1} belum dipilih`)
        if (Number(item.quantity || 0) <= 0)
          errors.push(`Ruahan baris ${idx + 1} qty harus > 0`)
        if (
          !hasEnoughStock(
            Number(item.quantity || 0),
            Number(item.stock || 0)
          )
        )
          errors.push(`Stok Ruahan baris ${idx + 1} tidak cukup`)
      })

      const reworkTotal = (batchData.reworkIngredients || []).reduce((acc, i) => acc + Number(i.quantity || 0), 0) +
        (batchData.reworkPremixes || []).reduce((acc, p) => acc + Number(p.quantity || 0), 0)

      const isOT = premixData.department?.toLowerCase() === 'ot'
      if (!isOT && Math.abs(reworkTotal - Number(batchData.batchSize || 0)) > 0.000001) {
        errors.push(
          `Total Rework Formula (${formatDecimalAuto(reworkTotal)}) harus sama dengan Batch Size (${formatDecimalAuto(batchData.batchSize || 0)})`
        )
      }
    } else {
      const isAllPremixIngredientStockEnough =
        !premixData?.premixIngredients.some(
          (premixIngredient) =>
            !hasEnoughStock(
              Number(premixIngredient.amount) *
                Number(batchData.batchSize ?? 0),
              Number(premixIngredient.stock)
            )
        )
      if (!isAllPremixIngredientStockEnough)
        errors.push('Stok bahan baku di ruahan tidak cukup')

      const isAllPremixPremixStockEnough = !premixData?.premixPremixes.some(
        (premixPremix) =>
          !hasEnoughStock(
            Number(premixPremix.amount) * Number(batchData.batchSize ?? 0),
            Number(premixPremix.stock)
          )
      )
      if (!isAllPremixPremixStockEnough) {
        errors.push('Stok ruahan di ruahan tidak cukup')
      }
      // Ensure each selected batch has enough stock and total matches requirement
      premixData?.premixPremixes.forEach((pp) => {
        const required = Number(pp.amount) * Number(batchData.batchSize ?? 0)
        const nestedEntries = (batchData.nestedPremixBatches || []).filter(
          (n) => n.childPremixId === pp.childPremixId
        )
        const selectedSum = nestedEntries.reduce(
          (sum, n) => sum + Number(n.quantity || 0),
          0
        )

        if (selectedSum < required - 0.000001) {
          errors.push(
            `Total qty terpilih untuk ruahan ${pp.childPremixId
            } (${formatDecimalAuto(selectedSum)}) kurang dari yang dibutuhkan (${formatDecimalAuto(required)})`
          )
        }

        if (selectedSum > required + 0.000001) {
          errors.push(
            `Total qty terpilih untuk ruahan ${pp.childPremixId
            } (${formatDecimalAuto(selectedSum)}) melebihi yang dibutuhkan (${formatDecimalAuto(required)})`
          )
        }

        const batchNumbers = nestedEntries
          .map((n) => n.batchNumber)
          .filter(Boolean)
        const uniqueBatchNumbers = new Set(batchNumbers)
        if (batchNumbers.length !== uniqueBatchNumbers.size) {
          errors.push(
            `Terdapat duplikat batch untuk ruahan ${pp.childPremixId}`
          )
        }

        nestedEntries.forEach((nb) => {
          if (!nb.batchNumber) {
            errors.push(
              `No. Batch belum dipilih untuk ruahan ${pp.childPremixId}`
            )
          }
          const options = nestedPremixBatchOptions[nb.childPremixId] || []
          const selectedBatch = options.find(
            (b) => b.batchNumber === nb.batchNumber
          )
          if (selectedBatch) {
            const available = Number(selectedBatch.currentQuantity || 0)
            const qtyUsedInThisBatch = Number(nb.quantity || 0)
            if (!hasEnoughStock(qtyUsedInThisBatch, available)) {
              errors.push(
                `Stok pada batch ${nb.batchNumber} untuk ruahan ${nb.childPremixId} tidak cukup`
              )
            }
          }
        })
      })
    }

    return errors
  }

  const isAllFieldsComplete = () => {
    return getValidationErrors().length === 0
  }

  React.useEffect(() => {
    API.get('products', '/premix/getBatches', {
      queryStringParameters: { productionId: productionData.productionId, pageSize: 100 },
    })
      .then((response: any) => {
        setRelatedBatches(response?.batches || response?.data || response || [])
      })
      .catch((error) => {})
  }, [productionData.productionId])

  React.useEffect(() => {
    if (productionData.premixId) {
      API.get(
        'products',
        `/premix/getPremix/${productionData.premixId}`,
        {}
      ).then((response) => {
        const data = response[0]
        setPremixData(data)

        // Fetch batches for nested premixes
        if (data && data.premixPremixes) {
          data.premixPremixes.forEach((pp: any) => {
            API.get('products', '/premix/getBatches', {
              queryStringParameters: {
                premixId: pp.childPremixId,
                onlyAvailable: true,
                pageSize: 100,
              },
            })
              .then((response: any) => {
                const batches =
                  response?.batches || response?.data || response || []
                setNestedPremixBatchOptions((prev) => ({
                  ...prev,
                  [pp.childPremixId]: batches,
                }))
              })
              .catch((error) => console.error(error))
          })
        }
      })
    }
  }, [productionData.premixId])

  const getBatchNumber = (
    productionLotDropdownValue: string | null,
    typeDropdownValue: string | null,
    digits: string = 'xxx'
  ) => {
    if (premixData.department?.toLowerCase() === 'ot') {
      if (productionLotDropdownValue === 'R' || productionLotDropdownValue === 'T') {
        return `${productionLotDropdownValue} ${selectedMonth}${selectedYear}${digits}`
      }
      return `${selectedMonth}${selectedYear}${digits}${productionLotDropdownValue ?? ''}`
    }

    return `${typeDropdownValue ?? ''}${selectedMonth}${selectedYear}${digits}${productionLotDropdownValue ?? ''}`
  }

  const handleAddReworkIngredient = () => {
    const current = batchData.reworkIngredients || []
    handleBatchFieldChange(
      [
        ...current,
        { ingredientId: '', ingredientName: '', quantity: 0, stock: 0 },
      ],
      'reworkIngredients'
    )
  }

  const handleDeleteReworkIngredient = (index: number) => {
    const current = batchData.reworkIngredients || []
    handleBatchFieldChange(
      current.filter((_, idx) => idx !== index),
      'reworkIngredients'
    )
  }

  const handleChangeReworkIngredient = (
    ingredient: IIngredient | null | undefined,
    index: number
  ) => {
    const current = batchData.reworkIngredients || []
    const updated = current.map((item, idx) =>
      idx === index
        ? {
          ...item,
          // If ingredient is cleared (null/undefined), reset fields
          ingredientId: ingredient ? ingredient.ingredientId : '',
          ingredientName: ingredient ? ingredient.ingredientName : '',
          stock: ingredient ? ingredient.stock : 0,
          quantity: ingredient ? item.quantity : 0,
        }
        : item
    )
    handleBatchFieldChange(updated, 'reworkIngredients')
  }

  const handleChangeReworkIngredientQty = (qty: number, index: number) => {
    const current = batchData.reworkIngredients || []
    const updated = current.map((item, idx) =>
      idx === index ? { ...item, quantity: qty } : item
    )
    handleBatchFieldChange(updated, 'reworkIngredients')
  }

  const handleAddReworkPremix = () => {
    const current = batchData.reworkPremixes || []
    handleBatchFieldChange(
      [
        ...current,
        {
          premixId: '',
          premixName: '',
          batchNumber: '',
          quantity: 0,
          stock: 0,
        },
      ],
      'reworkPremixes'
    )
  }

  const handleDeleteReworkPremix = (index: number) => {
    const current = batchData.reworkPremixes || []
    handleBatchFieldChange(
      current.filter((_, idx) => idx !== index),
      'reworkPremixes'
    )
    // Cleanup options
    const newOptions = { ...reworkPremixBatchOptions }
    delete newOptions[index]
    setReworkPremixBatchOptions(newOptions)
  }

  const handleChangeReworkPremix = (
    premix: IPremix | null | undefined,
    index: number
  ) => {
    const current = batchData.reworkPremixes || []
    const updated = current.map((item, idx) =>
      idx === index
        ? {
          ...item,
          premixId: premix ? premix.premixId : '',
          premixName: premix ? premix.premixName : '',
          batchNumber: '', // Reset batch selection when premix changes or cleared
          stock: 0,
          quantity: premix ? item.quantity : 0,
        }
        : item
    )
    handleBatchFieldChange(updated, 'reworkPremixes')

    // Fetch batches for this premix
    if (premix && premix.premixId) {
      API.get('products', '/premix/getBatches', {
        queryStringParameters: {
          premixId: premix.premixId,
          onlyAvailable: true,
          pageSize: 100,
        },
      })
        .then((response: any) => {
          const batches = response?.batches || response?.data || response || []
          setReworkPremixBatchOptions((prev) => ({ ...prev, [index]: batches }))
        })
        .catch((error) => console.error(error))
    } else {
      // If premix cleared, remove options for this index
      setReworkPremixBatchOptions((prev) => {
        const copy = { ...prev }
        delete copy[index]
        return copy
      })
    }
  }

  const handleChangeReworkPremixBatch = (
    batchNumber: string,
    index: number
  ) => {
    const current = batchData.reworkPremixes || []
    const options = reworkPremixBatchOptions[index] || []
    const selectedBatch = options.find((b) => b.batchNumber === batchNumber)

    const updated = current.map((item, idx) =>
      idx === index
        ? {
          ...item,
          batchNumber,
          stock: selectedBatch
            ? Number(selectedBatch.currentQuantity || 0)
            : 0,
        }
        : item
    )
    handleBatchFieldChange(updated, 'reworkPremixes')
  }

  const handleChangeReworkPremixQty = (qty: number, index: number) => {
    const current = batchData.reworkPremixes || []
    const updated = current.map((item, idx) =>
      idx === index ? { ...item, quantity: qty } : item
    )
    handleBatchFieldChange(updated, 'reworkPremixes')
  }

  const handleAddNestedBatch = (childPremixId: string, quantity: number) => {
    const current = batchData.nestedPremixBatches || []
    handleBatchFieldChange(
      [...current, { childPremixId, batchNumber: '', quantity }],
      'nestedPremixBatches'
    )
  }

  const handleRemoveNestedBatch = (index: number) => {
    const current = batchData.nestedPremixBatches || []
    handleBatchFieldChange(
      current.filter((_, idx) => idx !== index),
      'nestedPremixBatches'
    )
  }

  const handleChangeNestedBatch = (
    index: number,
    field: string,
    value: any
  ) => {
    const current = batchData.nestedPremixBatches || []
    const updated = current.map((item, idx) =>
      idx === index ? { ...item, [field]: value } : item
    )
    handleBatchFieldChange(updated, 'nestedPremixBatches')
  }

  React.useEffect(() => {
    if (isEditing && batchData.batchNumber && !batchData.batchNumber.endsWith('xxx')) {
      const template = batchData.batchNumber
      const prefixSpaceMatch = template.match(/^([RT])\s+(\d{4})(\d{3})/);

      if (prefixSpaceMatch) {
        setProductionLotDropdownValue(prefixSpaceMatch[1]);
        setSelectedMonth(prefixSpaceMatch[2].substring(0, 2));
        setSelectedYear(prefixSpaceMatch[2].substring(2, 4));
        setBatchDigits(prefixSpaceMatch[3]);
      } else {
        const hasPrefixLetter = /^[a-zA-Z]/.test(template)
        const startIdx = hasPrefixLetter ? 1 : 0
        const monthPart = template.substring(startIdx, startIdx + 2)
        const yearPart = template.substring(startIdx + 2, startIdx + 4)
        const digitPart = template.substring(startIdx + 4, startIdx + 7)

        setSelectedMonth(monthPart)
        setSelectedYear(yearPart)
        setBatchDigits(digitPart)

        if (hasPrefixLetter) {
          setTypeDropdownValue(template.substring(0, 1))
        }

        const lotVal = template.slice(-1)
        setProductionLotDropdownValue(lotVal)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing])

  React.useEffect(() => {
    handleBatchFieldChange(
      getBatchNumber(
        productionLotDropdownValue,
        typeDropdownValue,
        batchDigits || 'xxx'
      ),
      'batchNumber'
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productionLotDropdownValue, typeDropdownValue, batchDigits, selectedMonth, selectedYear])

  return (
    <Box>
      <Paper>
        <List>
          <ListItem>
            <Typography>
              <Box fontWeight="bold" display="inline">
                Kode Permintaan Ruahan:{' '}
              </Box>
              {productionData.productionDisplayId}
            </Typography>
          </ListItem>
          <ListItem>
            <Typography>
              <Box fontWeight="bold" display="inline">
                Kode + Nama Ruahan:{' '}
              </Box>
              {productionData.premixId} {productionData.premixName}
            </Typography>
          </ListItem>
          <ListItem>
            <Typography>
              <Box fontWeight="bold" display="inline">
                Requester:{' '}
              </Box>

              {batchData.batchNumber.endsWith('xxx')
                ? user.username
                : productionData.createdBy}
            </Typography>
          </ListItem>
          <ListItem>
            <Typography>
              <Box fontWeight="bold" display="inline">
                Qty Requested:{' '}
              </Box>
              {productionData.premixQuantity}{' '}
              {premixData.department?.toLowerCase() === 'ot' ? 'kapsul' : 'kg'}
            </Typography>
          </ListItem>
          <ListItem>
            <Typography>
              <Box fontWeight="bold" display="inline">
                Qty Requested Yang Sudah Ada PBOS:{' '}
              </Box>
              {relatedBatches
                .filter((b: IBatch) => !b.isVirtual)
                .reduce((acc: number, batch: IBatch) => {
                return acc + Number(batch.batchSize ?? 0)
              }, 0)}{' '}
              {premixData.department?.toLowerCase() === 'ot' ? 'kapsul' : 'kg'} (*Sum bets size
              dengan Kode Permintaan Ruahan yang sama)
            </Typography>
          </ListItem>
          <ListItem>
            <Box mt={4}>
              <Typography variant="h5">Bikin Bets</Typography>
            </Box>
          </ListItem>
          {premixData.department?.toLowerCase() !== 'ot' && (
            <ListItem>
              <Typography>
                <Box fontWeight="bold" display="inline" mr={1}>
                  Jenis Sediaan: *
                </Box>
              </Typography>
              <Select
                required
                value={typeDropdownValue}
                onChange={(e) => setTypeDropdownValue(String(e.target.value))}
              >
                {typeDropdownValues.map((value) => (
                  <MenuItem value={value.key}>
                    {value.key} - {value.value}
                  </MenuItem>
                ))}
              </Select>
            </ListItem>
          )}
          <ListItem>
            <Typography>
              <Box fontWeight="bold" display="inline" mr={1}>
                Lot Produksi: *
              </Box>
            </Typography>
            {premixData.department?.toLowerCase() === 'ot' ? (
              <TextField
                required
                value={productionLotDropdownValue ?? ''}
                onChange={(e) => {
                  const val = e.target.value.toUpperCase().slice(0, 1)
                  setProductionLotDropdownValue(val)
                }}
                helperText="Input 1 huruf (A-Z)"
              />
            ) : (
              <Select
                value={productionLotDropdownValue}
                onChange={(e) => {
                  setProductionLotDropdownValue(String(e.target.value))
                }}
              >
                {productionLotDropdownValues.map((value) => (
                  <MenuItem value={value.key}>
                    {value.key} - {value.value}
                  </MenuItem>
                ))}
              </Select>
            )}
          </ListItem>
          <ListItem>
            <Typography>
              <Box fontWeight="bold" display="inline" mr={1}>
                Bulan: *
              </Box>
            </Typography>
            <Select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(String(e.target.value))}
            >
              {[
                { key: '01', value: 'Januari' },
                { key: '02', value: 'Februari' },
                { key: '03', value: 'Maret' },
                { key: '04', value: 'April' },
                { key: '05', value: 'Mei' },
                { key: '06', value: 'Juni' },
                { key: '07', value: 'Juli' },
                { key: '08', value: 'Agustus' },
                { key: '09', value: 'September' },
                { key: '10', value: 'Oktober' },
                { key: '11', value: 'November' },
                { key: '12', value: 'Desember' },
              ].map((m) => (
                <MenuItem key={m.key} value={m.key}>
                  {m.key} - {m.value}
                </MenuItem>
              ))}
            </Select>
          </ListItem>
          <ListItem>
            <Typography>
              <Box fontWeight="bold" display="inline" mr={1}>
                Tahun: *
              </Box>
            </Typography>
            <Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(String(e.target.value))}
            >
              {Array.from({ length: 11 }, (_, i) => {
                const year = new Date().getFullYear() - 5 + i
                const yearStr2nd = year.toString().substring(2)
                return (
                  <MenuItem key={yearStr2nd} value={yearStr2nd}>
                    {year}
                  </MenuItem>
                )
              })}
            </Select>
          </ListItem>
          <ListItem>
            <TextField
              disabled={isEditing}
              required
              label="3 Digit No. Bets (xxx)"
              variant="outlined"
              fullWidth
              value={batchDigits}
              onChange={(e) => {
                const val = e.target.value.slice(0, 3)
                setBatchDigits(val)
              }}
              helperText="Input 3 digit angka terakhir (contoh: 001, 042)"
            />
          </ListItem>
          <ListItem>
            <Typography>
              <Box fontWeight="bold" display="inline" mr={1}>
                No. Bets:{' '}
                {getBatchNumber(
                  productionLotDropdownValue,
                  typeDropdownValue,
                  batchDigits || 'xxx'
                )}
              </Box>
            </Typography>
          </ListItem>
          <ListItem>
            <TextField
              disabled={isEditing && !isQuantityInitiallyNull}
              required
              autoComplete="off"
              variant="outlined"
              helperText={
                isEditing && !isQuantityInitiallyNull
                  ? 'Quantity hanya bisa berubah melalui kartu stok masuk/keluar'
                  : Number(batchData.batchSize) >= 1000000
                    ? 'Quantity tidak boleh lebih dari 1,000,000'
                    : ''
              }
              fullWidth
              type="number"
              label={
                premixData.department?.toLowerCase() === 'ot'
                  ? 'Batch Size (jumlah kapsul)'
                  : 'Batch Size (kg)'
              }
              value={batchData.batchSize}
              onChange={(e) =>
                handleBatchFieldChange(
                  Math.max(Number(e.target.value), 0),
                  'batchSize'
                )
              }
            />
          </ListItem>

          {(productionLotDropdownValue === 'R' || (premixData.department?.toLowerCase() === 'ot' && productionLotDropdownValue === 'T')) ? (
            <React.Fragment>
              <ListItem>
                <Box mt={4} width="100%">
                  <Grid container justifyContent="space-between" alignItems="center">
                    <Grid item>
                      <Typography variant="h5">
                        {premixData.department?.toLowerCase() === 'ot' && (productionLotDropdownValue === 'R' || productionLotDropdownValue === 'T')
                          ? 'Custom Formula: Bahan Baku'
                          : 'Rework Formula: Bahan Baku'}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleAddReworkIngredient}
                      >
                        Add Bahan
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </ListItem>
              {batchData.reworkIngredients?.map((item, index) => (
                <ListItem key={index}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={5}>
                      <LoadIngredientAutocomplete
                        name={
                          item.ingredientId
                            ? `${item.ingredientId} - ${item.ingredientName}`
                            : ''
                        }
                        onChange={(ing) =>
                          handleChangeReworkIngredient(ing, index)
                        }
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <Typography>Stock: {item.stock}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        label="Qty (KG)"
                        type="number"
                        fullWidth
                        value={item.quantity}
                        onChange={(e) =>
                          handleChangeReworkIngredientQty(
                            Number(e.target.value),
                            index
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={1}>
                      <IconButton onClick={() => handleDeleteReworkIngredient(index)} size="large">
                        <ClearIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </ListItem>
              ))}

              <ListItem>
                <Box mt={4} width="100%">
                  <Grid container justifyContent="space-between" alignItems="center">
                    <Grid item>
                      <Typography variant="h5">
                        {premixData.department?.toLowerCase() === 'ot' && (productionLotDropdownValue === 'R' || productionLotDropdownValue === 'T')
                          ? 'Custom Formula: Ruahan'
                          : 'Rework Formula: Ruahan'}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleAddReworkPremix}
                      >
                        Add Ruahan
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </ListItem>
              {batchData.reworkPremixes?.map((item, index) => (
                <ListItem key={index}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={3}>
                      <LoadPremixAutocomplete
                        name={
                          item.premixId
                            ? `${item.premixId} - ${item.premixName}`
                            : ''
                        }
                        onChange={(p) => handleChangeReworkPremix(p, index)}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <Select
                        fullWidth
                        value={item.batchNumber}
                        onChange={(e) =>
                          handleChangeReworkPremixBatch(
                            String(e.target.value),
                            index
                          )
                        }
                        displayEmpty
                      >
                        <MenuItem value="" disabled>
                          Select Batch
                        </MenuItem>
                        {reworkPremixBatchOptions[index]?.map((batch) => (
                          <MenuItem
                            key={batch.batchNumber}
                            value={batch.batchNumber}
                          >
                            {batch.batchNumber} (Stock: {batch.currentQuantity})
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>
                    <Grid item xs={2}>
                      <Typography>Stock: {item.stock}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        label={
                          premixData.department?.toLowerCase() === 'ot' &&
                          (productionLotDropdownValue === 'R' ||
                            productionLotDropdownValue === 'T')
                            ? 'Qty (pcs)'
                            : 'Qty (KG)'
                        }
                        type="number"
                        fullWidth
                        value={item.quantity}
                        onChange={(e) =>
                          handleChangeReworkPremixQty(
                            Number(e.target.value),
                            index
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={1}>
                      <IconButton onClick={() => handleDeleteReworkPremix(index)} size="large">
                        <ClearIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </ListItem>
              ))}
              {premixData.department?.toLowerCase() !== 'ot' && (
                <ListItem>
                  <Box mt={2}>
                    <Typography
                      variant="h6"
                      color={
                        Math.abs(
                          (batchData.reworkIngredients || []).reduce(
                            (acc, i) => acc + Number(i.quantity || 0),
                            0
                          ) +
                            (batchData.reworkPremixes || []).reduce(
                              (acc, p) => acc + Number(p.quantity || 0),
                              0
                            ) -
                            Number(batchData.batchSize || 0)
                        ) > 0.000001
                          ? 'error'
                          : 'primary'
                      }
                    >
                      Total{' '}
                      {premixData.department?.toLowerCase() === 'ot' &&
                      (productionLotDropdownValue === 'R' ||
                        productionLotDropdownValue === 'T')
                        ? 'Custom Formula'
                        : 'Rework'}
                      :{' '}
                      {formatDecimalAuto(
                        (batchData.reworkIngredients || []).reduce(
                          (acc, i) => acc + Number(i.quantity || 0),
                          0
                        ) +
                        (batchData.reworkPremixes || []).reduce(
                          (acc, p) => acc + Number(p.quantity || 0),
                          0
                        )
                      )}{' '}
                      KG (Batch Size:{' '}
                      {formatDecimalAuto(batchData.batchSize || 0)} KG)
                    </Typography>
                  </Box>
                </ListItem>
              )}
            </React.Fragment>
          ) : (
            <React.Fragment>
              <ListItem>
                <Box mt={4}>
                  <Typography variant="h5">
                    {' '}
                    Daftar Bahan Baku di Ruahan
                  </Typography>
                </Box>
              </ListItem>
              <ListItem>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Typography>Kode + Nama Bahan Baku</Typography>{' '}
                        </TableCell>
                        <TableCell>
                          <Typography>Available stock</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {premixData.department?.toLowerCase() === 'ot'
                              ? 'Qty Yang Diperlukan (mg)'
                              : 'Qty Yang Diperlukan (kg)'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>Apakah Qty Cukup?</Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {premixData?.premixIngredients?.map(
                        (premixIngredient) => (
                          <TableRow key={premixIngredient.ingredientId}>
                            <TableCell>
                              <Typography>
                                {premixIngredient.ingredientId} -{' '}
                                {premixIngredient.ingredientName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography>{premixIngredient.stock}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography>
                                {formatDecimalAuto(
                                  Number(premixIngredient.amount) *
                                  Number(batchData.batchSize ?? 0)
                                )}{' '}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {hasEnoughStock(
                                Number(premixIngredient.amount) *
                                  Number(batchData.batchSize ?? 0),
                                Number(premixIngredient.stock)
                              ) ? (
                                <Typography color="primary">✓</Typography>
                              ) : (
                                <Typography color="error">✖</Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        )
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </ListItem>

              <ListItem>
                <Box mt={4}>
                  <Typography variant="h5"> Daftar Ruahan di Ruahan</Typography>
                </Box>
              </ListItem>
              <ListItem>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Typography>Kode + Nama Ruahan</Typography>{' '}
                        </TableCell>
                        <TableCell>
                          <Typography>Pilih Batch</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>
                            Available stock untuk seluruh ruahan
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {premixData.department?.toLowerCase() === 'ot'
                              ? 'Qty Yang Diperlukan (mg)'
                              : 'Qty Yang Diperlukan (kg)'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>Apakah Qty Cukup?</Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {premixData?.premixPremixes?.map((premixPremix) => {
                        const options =
                          nestedPremixBatchOptions[
                          premixPremix.childPremixId
                          ] || []
                        const nestedEntries = (
                          batchData.nestedPremixBatches || []
                        )
                          .map((entry, originalIndex) => ({
                            ...entry,
                            originalIndex,
                          }))
                          .filter(
                            (entry) =>
                              entry.childPremixId === premixPremix.childPremixId
                          )

                        const requiredQty =
                          Number(premixPremix.amount) *
                          Number(batchData.batchSize ?? 0)

                        const selectedQtySum = nestedEntries.reduce(
                          (sum, entry) => sum + Number(entry.quantity || 0),
                          0
                        )

                        return (
                          <TableRow key={premixPremix.childPremixId}>
                            <TableCell>
                              <Typography>
                                {premixPremix.childPremixId} -{' '}
                                {premixPremix.childPremixName}
                              </Typography>
                              <Box mt={1}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  startIcon={<AddIcon />}
                                  onClick={() =>
                                    handleAddNestedBatch(
                                      premixPremix.childPremixId,
                                      Math.max(0, requiredQty - selectedQtySum)
                                    )
                                  }
                                >
                                  Add Batch
                                </Button>
                              </Box>
                            </TableCell>
                            <TableCell>
                              {nestedEntries.map((entry) => (
                                <Box
                                  key={entry.originalIndex}
                                  display="flex"
                                  alignItems="center"
                                  mb={1}
                                >
                                  <Box flexGrow={1} mr={1}>
                                    <FormControl fullWidth>
                                      <Select
                                        value={entry.batchNumber || ''}
                                        onChange={(e) =>
                                          handleChangeNestedBatch(
                                            entry.originalIndex,
                                            'batchNumber',
                                            String(e.target.value)
                                          )
                                        }
                                      >
                                        <MenuItem value="" disabled>
                                          Select Batch
                                        </MenuItem>
                                        {options.map((opt) => (
                                          <MenuItem
                                            key={opt.batchNumber}
                                            value={opt.batchNumber}
                                          >
                                            {opt.batchNumber} (Stok:{' '}
                                            {opt.currentQuantity})
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Box>
                                  <Box width={100} mr={1}>
                                    <TextField
                                      type="number"
                                      label="Qty (KG)"
                                      value={entry.quantity}
                                      onChange={(e) =>
                                        handleChangeNestedBatch(
                                          entry.originalIndex,
                                          'quantity',
                                          Number(e.target.value)
                                        )
                                      }
                                    />
                                  </Box>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleRemoveNestedBatch(
                                        entry.originalIndex
                                      )
                                    }
                                  >
                                    <ClearIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              ))}
                              {nestedEntries.length === 0 && (
                                <Typography
                                  color="textSecondary"
                                  variant="caption"
                                >
                                  Belum ada batch dipilih
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography>{premixPremix.stock}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography>{requiredQty.toFixed(6)} </Typography>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                Terpilih: {selectedQtySum.toFixed(6)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {hasEnoughStock(requiredQty, selectedQtySum) ? (
                                <Typography color="primary">✓</Typography>
                              ) : (
                                <Typography color="error">✖</Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </ListItem>
            </React.Fragment>
          )}
          <ListItem>
            <Typography>PBOS Created By: {user.username}</Typography>
          </ListItem>
        </List>
        {isLoading && (
          <Box p={3}>
            <CircularProgress />
          </Box>
        )}
      </Paper>
      <React.Fragment>
        <Box mt={3} />
      </React.Fragment>
      <Paper>
        <Box p={2} mt={3}>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={!isAllFieldsComplete() || isLoading}
            onClick={isEditing ? updateBatch : saveBatch}
          >
            Save
          </Button>
          {!isAllFieldsComplete() && (
            <Box mt={1}>
              {getValidationErrors().map((err) => (
                <Typography
                  key={err}
                  variant="caption"
                  color="error"
                  display="block"
                >
                  • {err}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
